/**
 * Production-safe logger utility
 * Logs only in development mode, silent in production
 */

const isDevelopment = process.env.NODE_ENV === 'development';

interface Logger {
    log: (...args: unknown[]) => void;
    error: (...args: unknown[]) => void;
    warn: (...args: unknown[]) => void;
    info: (...args: unknown[]) => void;
    debug: (...args: unknown[]) => void;
    group: (...args: unknown[]) => void;
    groupEnd: () => void;
    groupCollapsed: (...args: unknown[]) => void;
}

/**
 * Logger that only outputs in development mode
 * In production, all methods are no-ops
 */
export const logger: Logger = {
    log: (...args: unknown[]) => {
        if (isDevelopment) {
            console.log(...args);
        }
    },
    error: (...args: unknown[]) => {
        if (isDevelopment) {
            console.error(...args);
        }
    },
    warn: (...args: unknown[]) => {
        if (isDevelopment) {
            console.warn(...args);
        }
    },
    info: (...args: unknown[]) => {
        if (isDevelopment) {
            console.info(...args);
        }
    },
    debug: (...args: unknown[]) => {
        if (isDevelopment) {
            console.debug(...args);
        }
    },
    group: (...args: unknown[]) => {
        if (isDevelopment) {
            console.group(...args);
        }
    },
    groupEnd: () => {
        if (isDevelopment) {
            console.groupEnd();
        }
    },
    groupCollapsed: (...args: unknown[]) => {
        if (isDevelopment) {
            console.groupCollapsed(...args);
        }
    },
};

/**
 * Alternative: Keep console.error in production but with sanitized messages
 * This can be useful for error tracking services
 */
export const productionLogger = {
    error: (message: string, context?: Record<string, unknown>) => {
        // Always log errors, but sanitize sensitive data
        const sanitizedContext = context ? {
            ...context,
            // Remove potential sensitive fields
            password: undefined,
            token: undefined,
            apiKey: undefined,
            email: context.email ? '***@***.***' : undefined,
        } : undefined;

        console.error(message, sanitizedContext);
    },
};

export default logger;
